import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Megaphone, X, Trophy, Sparkles, Video } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Polls /api/broadcasts/unread every 30s and renders any unread broadcasts as
 * a stacked popup overlay. Each one is dismissed via the API so it never shows
 * again to that user. Mounted globally in Layout.
 */
export default function BroadcastListener() {
  const [items, setItems] = useState([]);
  const seenRef = useRef(new Set());

  const fetchUnread = async () => {
    try {
      const { data } = await api.get("/broadcasts/unread");
      // append new ones we haven't already shown this session
      const fresh = data.filter((b) => !seenRef.current.has(b.id));
      fresh.forEach((b) => seenRef.current.add(b.id));
      if (fresh.length) setItems((prev) => [...prev, ...fresh]);
    } catch (e) {
      // silently fail; auth handled by interceptor
    }
  };

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, []);

  const dismiss = async (bid) => {
    setItems((prev) => prev.filter((b) => b.id !== bid));
    try {
      await api.post(`/broadcasts/${bid}/dismiss`);
    } catch (e) { /* ok */ }
  };

  if (!items.length) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="broadcast-overlay">
      <div className="bg-white border-4 border-[#0A0A0A] max-w-lg w-full p-8 relative animate-in fade-in zoom-in-95 duration-300" data-testid={`broadcast-popup-${items[0].id}`}>
        <button
          onClick={() => dismiss(items[0].id)}
          className="absolute top-3 right-3 text-[#525252] hover:text-[#0A0A0A]"
          data-testid={`broadcast-dismiss-${items[0].id}`}
          aria-label="Dismiss"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-3 label-eyebrow text-[#2596be]">
          {items[0].kind === "announcement" ? <Trophy size={14} />
            : items[0].kind === "motivational" ? <Sparkles size={14} />
            : <Megaphone size={14} />}
          {items[0].kind === "challenge-live" ? "Challenge live"
            : items[0].kind === "announcement" ? "Announcement"
            : "Heads up"}
        </div>
        <div className="font-display text-3xl font-black tracking-tighter mt-3 leading-tight">
          {items[0].kind === "challenge-live" ? "A challenge is live!"
            : items[0].kind === "announcement" ? "Result announced"
            : "Message from faculty"}
        </div>
        <p className="mt-4 text-base text-[#0A0A0A] leading-relaxed whitespace-pre-wrap">{items[0].message}</p>
        <div className="mt-6 flex justify-end gap-2">
          {items[0].meeting_link && (
            <Button
              onClick={() => { window.open(items[0].meeting_link, "_blank", "noopener"); dismiss(items[0].id); }}
              className="rounded-none bg-[#A23232] hover:bg-[#8a2828]"
              data-testid={`broadcast-join-${items[0].id}`}
            >
              <Video size={14} className="mr-2" /> Join meeting
            </Button>
          )}
          <Button onClick={() => dismiss(items[0].id)} className="rounded-none bg-[#0A0A0A] hover:bg-[#2596be]" data-testid={`broadcast-ok-${items[0].id}`}>
            Got it
          </Button>
        </div>
        {items.length > 1 && (
          <div className="mt-3 text-[10px] uppercase tracking-wider text-[#525252] text-right" data-testid="broadcast-stack-count">
            +{items.length - 1} more to view
          </div>
        )}
      </div>
    </div>
  );
}
